import { useState, useEffect, useRef } from 'react';

/**
 * DeleteTierModal - Specialized modal for deleting tiers with subscriber migration
 * 
 * Features:
 * - Shows warning about potential subscribers
 * - Optionally shows migration tier selection
 * - Validates migration tier selection if provided
 * - Keyboard navigation (Escape to close)
 * - Focus management
 * 
 * @param {boolean} isOpen - Whether modal is visible
 * @param {Function} onClose - Handler for closing modal
 * @param {Function} onConfirm - Handler for confirming delete (tierId, migrateToId)
 * @param {object} tier - Tier to delete
 * @param {Array} availableTiers - List of tiers available for migration (excluding current tier)
 * @param {boolean} isProcessing - Whether deletion is in progress
 * @param {string} [errorMessage] - Error message from parent (e.g., backend validation error)
 */
export default function DeleteTierModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  tier,
  availableTiers = [],
  isProcessing = false,
  errorMessage = null
}) {
  const [selectedMigrateTo, setSelectedMigrateTo] = useState('');
  const deleteButtonRef = useRef(null);

  // Reset migration selection when modal opens
  useEffect(() => {
    if (isOpen && tier) {
      setSelectedMigrateTo('');
    }
  }, [isOpen, tier]);

  // Focus delete button when modal opens
  useEffect(() => {
    if (isOpen && deleteButtonRef.current) {
      deleteButtonRef.current.focus();
    }
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isProcessing) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, isProcessing, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isProcessing) {
      onClose();
    }
  };

  const handleConfirm = () => {
    const migrateToValue = selectedMigrateTo || null;
    onConfirm(tier.id, migrateToValue);
  };

  if (!isOpen || !tier) return null;

  // Filter out current tier and inactive tiers from migration options
  const migrationOptions = availableTiers.filter(
    (t) => t.id !== tier.id && t.status === 'active'
  );

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
      aria-describedby="delete-modal-description"
    >
      <div 
        className="bg-dark-800 border border-dark-700 rounded-lg p-6 max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <h2 
          id="delete-modal-title"
          className="text-xl font-bold text-white mb-4"
        >
          Delete Tier
        </h2>

        {/* Error message from parent (backend validation errors) */}
        {errorMessage && (
          <div className="mb-4 p-4 bg-red-900/20 border border-red-500/30 rounded-sm text-red-400 text-sm" role="alert">
            {errorMessage}
          </div>
        )}

        {/* Message */}
        <p 
          id="delete-modal-description"
          className="text-gray-300 mb-4"
        >
          Are you sure you want to delete <span className="font-semibold text-white">"{tier.name}"</span>?
          <br /><br />
          This action cannot be undone. If this tier has active subscribers, you must select a tier to migrate them to.
        </p>

        {/* Migration tier selection (optional) */}
        <div className="mb-6">
          <label htmlFor="migrate-to-tier" className="block text-sm font-medium text-gray-300 mb-2">
            Migrate subscribers to (optional)
          </label>
          <select
            id="migrate-to-tier"
            value={selectedMigrateTo}
            onChange={(e) => setSelectedMigrateTo(e.target.value)}
            className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={isProcessing}
          >
            <option value="">-- No migration (tier must have no subscribers) --</option>
            {migrationOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.price_monthly_cents === 0 ? 'Free' : `$${(t.price_monthly_cents / 100).toFixed(2)}/mo`})
              </option>
            ))}
          </select>
          {migrationOptions.length === 0 && (
            <p className="mt-2 text-sm text-yellow-400" role="alert">
              No active tiers available for migration. If this tier has subscribers, deletion will fail. Create or activate another tier first.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 border border-dark-600 text-gray-300 rounded-lg hover:bg-dark-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
          >
            Cancel
          </button>
          <button
            ref={deleteButtonRef}
            onClick={handleConfirm}
            disabled={isProcessing}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
          >
            {isProcessing ? 'Deleting...' : 'Delete Tier'}
          </button>
        </div>
      </div>
    </div>
  );
}
